import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import io from "socket.io-client";
import Layout from "../components/Layout";
import ConfirmationModal from "../components/ConfirmationModal";
import { COLORS } from "../components/TipOfDay/colors";
import {
  approveWinPremiumSubmission,
  getWinPremiumActive,
  getWinPremiumSubmissions,
  rejectWinPremiumSubmission,
  setWinPremiumActive,
} from "../service/service";
import { BASE_URL } from "../service/config";

const STATUS_TABS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

const APPROVE_COLOR = "#26A987";

// DD-MM-YYYY like the rest of the portal, plus the time — the queue is worked in arrival order.
const formatDateTime = (isoDate) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()} ${hours}:${minutes}`;
};

const formatDuration = (totalSeconds) => {
  const seconds = Math.max(0, Math.round(totalSeconds || 0));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

// The backend's error handler sends { err }; validation errors send { success: false, err }.
const errorMessage = (result, fallback) => result?.error?.err || result?.error?.message || fallback;

// What approval gave: 3 days of Premium, or nothing because the user already had Premium.
const rewardLabel = (reward) => {
  if (reward?.expiresAt) return `Until ${formatDateTime(reward.expiresAt)}`;
  if (reward?.skippedReason === "already_premium") return "Already had Premium";
  return "—";
};

// user is null when the account no longer exists — the recording is kept, the person isn't.
const displayName = (user) => {
  if (!user) return "Deleted account";
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return fullName || user.username || user.email || "Unnamed user";
};

const SubmissionRow = ({ submission, status, busyId, onApprove, onReject }) => {
  const [reason, setReason] = useState("");
  const { user } = submission;
  const name = displayName(user);
  const accountGone = !user || Boolean(submission.accountDeletedAt);
  const busy = busyId === submission._id;

  return (
    <TableRow sx={{ "& > .MuiTableCell-root": { py: 1.5 } }}>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar src={user?.profileUrl || undefined} sx={{ width: 32, height: 32, fontSize: 14 }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {name}
            </Typography>
            {user && (
              <Typography variant="caption" color="text.secondary">
                {user.username ? `@${user.username}` : user.email}
              </Typography>
            )}
            {accountGone && user && (
              <Chip label="Account deleted" size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>{submission.accent}</TableCell>
      <TableCell>
        {/* preload="none" — nothing downloads until the coach presses play. */}
        <audio controls preload="none" src={submission.audioUrl} style={{ width: 260, height: 36 }} />
        <Typography variant="caption" color="text.secondary" component="div">
          {formatDuration(submission.durationSec)}
        </Typography>
      </TableCell>
      <TableCell>{formatDateTime(submission.createdAt)}</TableCell>

      {status === "pending" ? (
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ConfirmationModal
              title="Approve this recording?"
              description={`${name} · ${submission.accent}. This can't be undone.`}
              confirmLabel="Approve"
              cancelLabel="Cancel"
              loading={busy}
              onConfirm={() => onApprove(submission)}
              renderTrigger={({ openModal, loading }) => (
                <Button
                  variant="contained"
                  size="small"
                  disableElevation
                  sx={{ bgcolor: APPROVE_COLOR, color: "#fff", "&:hover": { bgcolor: "#1E8A6E" } }}
                  onClick={openModal}
                  // The backend refuses: the reward needs an account to land on.
                  disabled={loading || accountGone}
                >
                  Approve
                </Button>
              )}
            />
            <ConfirmationModal
              title="Reject this recording?"
              description={`${name} · ${submission.accent}. They'll get a notification and can record again.`}
              confirmLabel="Reject"
              cancelLabel="Cancel"
              loading={busy}
              destructive
              onConfirm={() => onReject(submission, reason.trim())}
              renderTrigger={({ openModal, loading }) => (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ color: COLORS.destructive, borderColor: COLORS.destructive }}
                  onClick={openModal}
                  disabled={loading}
                >
                  Reject
                </Button>
              )}
            >
              <TextField
                label="Reason (optional)"
                helperText="Sent to the user in their rejection notification, exactly as written."
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                inputProps={{ maxLength: 500 }}
                multiline
                minRows={2}
                fullWidth
                sx={{ mt: 2 }}
              />
            </ConfirmationModal>
            {busy && <CircularProgress size={18} />}
          </Box>
        </TableCell>
      ) : (
        <TableCell>{formatDateTime(submission.reviewedAt)}</TableCell>
      )}
      {status === "rejected" && <TableCell>{submission.rejectionReason || "—"}</TableCell>}
      {status === "approved" && <TableCell>{rewardLabel(submission.reward)}</TableCell>}
    </TableRow>
  );
};

const RecordAndEarn = () => {
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [activeSaving, setActiveSaving] = useState(false);

  const fetchSubmissions = useCallback(
    async ({ showLoading = true } = {}) => {
      if (showLoading) setLoading(true);
      const result = await getWinPremiumSubmissions({ status, page: page + 1, limit: rowsPerPage });
      if (result?.success) {
        const items = result.data?.items || [];
        // Deciding the last row on a later page leaves it empty — step back a page.
        if (!items.length && page > 0) {
          setPage(page - 1);
          return;
        }
        setSubmissions(items);
        setTotal(result.data?.total || 0);
        setCounts((prev) => ({ ...prev, ...result.data?.counts }));
      }
      if (showLoading) setLoading(false);
    },
    [status, page, rowsPerPage]
  );

  const fetchActiveStatus = useCallback(async () => {
    const result = await getWinPremiumActive();
    if (result?.success) {
      setIsActive(result.data?.enabled !== false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    fetchActiveStatus();
  }, [fetchActiveStatus]);

  // Live: the backend broadcasts "winPremiumSubmissionsChanged" when a recording arrives or
  // another coach approves/rejects one. No payload — re-fetch quietly so the table and any
  // playing audio don't flash. Held in a ref so the socket stays mount-only.
  const fetchSubmissionsRef = useRef(fetchSubmissions);
  fetchSubmissionsRef.current = fetchSubmissions;

  useEffect(() => {
    const socket = io(BASE_URL, { transports: ["websocket"] });
    socket.on("winPremiumSubmissionsChanged", () => {
      fetchSubmissionsRef.current({ showLoading: false });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleToggleActive = async (event) => {
    const nextEnabled = event.target.checked;
    setActiveSaving(true);
    const result = await setWinPremiumActive(nextEnabled);
    setActiveSaving(false);
    if (result?.success) {
      setIsActive(nextEnabled);
    } else {
      setActionError(errorMessage(result, "Couldn't change Record & Earn. Please try again."));
    }
  };

  const decide = async (submission, request, fallback) => {
    setActionError("");
    setBusyId(submission._id);
    const result = await request();
    setBusyId(null);
    if (!result?.success) {
      // e.g. another coach decided it first, or the user deleted their account.
      setActionError(errorMessage(result, fallback));
    }
    await fetchSubmissions({ showLoading: false });
  };

  const handleApprove = (submission) =>
    decide(submission, () => approveWinPremiumSubmission(submission._id), "Couldn't approve this recording.");

  const handleReject = (submission, reason) =>
    decide(submission, () => rejectWinPremiumSubmission(submission._id, reason), "Couldn't reject this recording.");

  const columnCount = status === "pending" ? 5 : 6;

  return (
    <Layout>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Record & Earn
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Listen to users' natural-accent recordings and approve or reject them.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch checked={isActive} onChange={handleToggleActive} disabled={activeSaving} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {isActive ? "Record & Earn is on" : "Record & Earn is off"}
          </Typography>
        </Box>
      </Box>

      {!isActive && (
        <Typography variant="body2" sx={{ color: COLORS.destructive, mb: 2 }}>
          Record & Earn is turned off — the offer is hidden in the app and new recordings are refused.
          Recordings already sent can still be reviewed below.
        </Typography>
      )}

      {actionError && (
        <Alert severity="error" onClose={() => setActionError("")} sx={{ mb: 2 }}>
          {actionError}
        </Alert>
      )}

      <Tabs
        value={status}
        onChange={(event, value) => {
          setStatus(value);
          setPage(0);
        }}
        sx={{ mb: 2 }}
      >
        {STATUS_TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={`${tab.label} (${counts[tab.value] ?? 0})`} />
        ))}
      </Tabs>

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Accent</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Recording</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>{status === "pending" ? "Actions" : "Reviewed"}</TableCell>
              {status === "rejected" && <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>}
              {status === "approved" && <TableCell sx={{ fontWeight: 700 }}>Premium</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            )}
            {!loading && submissions.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ color: "text.secondary", py: 3 }}>
                  {status === "pending" ? "No recordings waiting for review." : `No ${status} recordings yet.`}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              submissions.map((submission) => (
                <SubmissionRow
                  key={submission._id}
                  submission={submission}
                  status={status}
                  busyId={busyId}
                  onApprove={handleApprove}
                  onReject={handleReject}
                />
              ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
        />
      </TableContainer>
    </Layout>
  );
};

export default RecordAndEarn;
